import { yupResolver } from "@hookform/resolvers/yup";
import { Close } from "@mui/icons-material";
import {
  Pagination,
  Paper,
  Table,
  TableCell,
  TableContainer,
  TableFooter,
  TablePagination,
  TableRow,
} from "@mui/material";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { COMPONENT_IDS } from "../../helpers/constants/componentIds";
import { commonSiteCoreKeys } from "../../helpers/constants/siteCoreKeys";
import useIsMounted from "../../hooks/useIsMounted";
import { useNavigationSiteCore } from "../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../hooks/useSiteCore";
import { MuiBox } from "../box/MuiBox";
import { MuiButtonSecondary } from "../button/MuiButton";
import { FormController } from "../formController/FormController";
import { MuiTableWrapper } from "../table/MuiTable.style";
import { MuiTextField } from "../textField/MuiTextfield";
import {
  TimelineActivity,
  TimelineActivityType,
} from "../timelineActivity/TimelineActivity";
import { MuiStyledTooltip } from "../tooltip/MuiToolTip.style";
import { MuiTypography } from "../typography/MuiTypography";
import { TimelineActivityWrapper } from "./TimelineActivityTable.style";
import { isNotEmpty } from "../../helpers/utils";
import { useMediaQueries } from "../../hooks/useMediaQueries";

interface FooterPaginationProps {
  pageId: string;
  handleEdit?: (id: string) => void;
  handleDelete?: (id: string) => void;
  activityData: TimelineActivityType[];
  isTodaySelected?: boolean;
  displayWith?: boolean;
  noResultMessage?: string;
  noDataInApi: boolean;
  emptyMessage?: string;
  hasDeletePermission?: boolean;
}

export const TimelineActivityTable = forwardRef(
  (props: FooterPaginationProps, ref) => {
    const [rowsPerPage, setRowsPerPage] = useState<number>(10);
    const [page, setPage] = useState<number>(0);
    const { commonLabels, fetchValidationMessage } = useSiteCore();
    const { commonARIALabel } = useNavigationSiteCore();
    const { isMobile } = useMediaQueries();

    const {
      activityData,
      handleEdit,
      handleDelete,
      pageId,
      noDataInApi,
      emptyMessage = `${commonLabels.noResult}`,
      noResultMessage = `${commonLabels.searchCriteria}`,
      isTodaySelected,
      displayWith,
      hasDeletePermission = true,
    } = props;

    const goToPageRef = useRef<HTMLInputElement | null>(null);

    const validPageNumberMessage = fetchValidationMessage(
      commonSiteCoreKeys.enterValidPageNumber,
    );

    const schema = yup.object().shape({
      tablePaginationPageNo: yup
        .number()
        .typeError(validPageNumberMessage)
        .positive(validPageNumberMessage)
        .integer(validPageNumberMessage)
        .required(fetchValidationMessage(commonSiteCoreKeys.pageNumberRequired))
        .test("is-valid", validPageNumberMessage, (value) => {
          const totalPages = Math.ceil([...activityData].length / rowsPerPage);

          if (value <= 0 || value > totalPages) {
            return false;
          }

          return true;
        }),
    });

    const {
      getValues,
      control,
      reset,
      watch,
      formState: { errors },
    } = useForm({
      mode: "all",
      defaultValues: {
        tablePaginationPageNo: 1,
      },
      resolver: yupResolver(schema),
    });

    const { isMounted } = useIsMounted(); // Track if the component is mounted

    const [showToolTip, setShowToolTip] = useState(true);

    useEffect(() => {
      setTimeout(() => {
        setShowToolTip(!!errors.tablePaginationPageNo);
      }, 0);
    }, [watch("tablePaginationPageNo")]);

    const handleToolTipClose = () => setShowToolTip(false);

    const goToPage = () => {
      const { tablePaginationPageNo } = getValues();
      const totalPages = Math.ceil([...activityData].length / rowsPerPage);

      if (
        !tablePaginationPageNo ||
        tablePaginationPageNo === null ||
        tablePaginationPageNo <= 0 ||
        tablePaginationPageNo > totalPages
      ) {
        setShowToolTip(true);
        return;
      }
      // starts from 0
      setPage(+tablePaginationPageNo - 1);
    };

    const handleChangePage = (
      event: React.MouseEvent<HTMLButtonElement> | null,
      newPage: number,
    ) => {
      setPage(newPage);
    };

    const handleChangeRowsPerPage = (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
    };

    //   call below method on reset pagination
    const resetPagination = () => {
      setPage(0);
      reset();
    };

    useImperativeHandle(ref, () => ({
      resetPagination,
    }));

    const handlePageChange = (
      event: React.ChangeEvent<unknown>,
      newPage: number,
    ) => {
      setPage(newPage - 1);
    };

    useEffect(() => {
      // Focus the input if the component is mounted
      if (isMounted) {
        goToPageRef.current?.focus();
      }
    }, [errors.tablePaginationPageNo]);

    const getCreatorValue = (row: TimelineActivityType) => {
      if (!displayWith && isNotEmpty(row?.instructor)) {
        return row?.instructor;
      }
      return row.creator;
    };

    const getTimeStampValue = (row: TimelineActivityType) => {
      if (isTodaySelected && isNotEmpty(row?.fromToTimeDuration)) {
        return row?.fromToTimeDuration;
      }
      return row.timeStamp;
    };

    const getHelperText = () => {
      if (errors.tablePaginationPageNo) {
        return validPageNumberMessage;
      }
      return "";
    };

    const renderEmptyTableBody = () => {
      return (
        <MuiBox className="no-result">
          <MuiTypography
            variant="h3"
            align="center"
            data-testid={COMPONENT_IDS.COMMON_TABLE_NO_RESULT}
          >
            {noDataInApi ? emptyMessage : noResultMessage}
          </MuiTypography>
        </MuiBox>
      );
    };

    const renderPagination = () => {
      return (
        activityData.length > 0 && (
          <MuiTableWrapper className="pagination-wrapper">
            <TableContainer component={Paper} id="activity-table">
              <Table>
                <TableFooter>
                  <TableRow key="footer">
                    {!isMobile && (
                      <TablePagination
                        id={`${pageId}-tablePagination`}
                        rowsPerPageOptions={[10, 30, 50, 100]}
                        colSpan={2}
                        count={[...activityData].length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        slotProps={{
                          select: {
                            inputProps: {
                              "aria-label": "rows per page",
                            },
                            native: true,
                          },
                        }}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        labelRowsPerPage={commonLabels.rowsPerPage}
                      />
                    )}
                    <TableCell colSpan={4}>
                      <MuiBox className="goto-page-wrapper">
                        <MuiBox className="pagination-container">
                          <Pagination
                            count={Math.ceil(
                              [...activityData].length / rowsPerPage,
                            )}
                            shape="rounded"
                            page={page + 1}
                            onChange={handlePageChange}
                            siblingCount={isMobile ? 0 : 1}
                            boundaryCount={1}
                          />
                        </MuiBox>
                        <MuiBox className="go-to-controls">
                          <MuiTypography variant="h3">
                            {commonLabels.goToPage}
                          </MuiTypography>
                          <FormController
                            control={control}
                            name="tablePaginationPageNo"
                          >
                            {errors.tablePaginationPageNo ? (
                              <MuiStyledTooltip
                                placement="top"
                                title={
                                  <>
                                    <Close
                                      onClick={(e) => handleToolTipClose()}
                                    />
                                    <MuiTypography>
                                      {errors.tablePaginationPageNo.message}
                                    </MuiTypography>
                                  </>
                                }
                                open={showToolTip}
                              >
                                <MuiTextField
                                  id={`${pageId}-tablePaginationPageNo`}
                                  className="hide-error"
                                  inputRef={goToPageRef}
                                  value={getValues("tablePaginationPageNo")}
                                  autoFocus={!!errors.tablePaginationPageNo}
                                  type="number"
                                  inputNumberWithoutDecimal
                                  requiredARIALabel={commonARIALabel.required}
                                />
                              </MuiStyledTooltip>
                            ) : (
                              <MuiTextField
                                id={`${pageId}-tablePaginationPageNo`}
                                inputRef={goToPageRef}
                                value={getValues("tablePaginationPageNo")}
                                autoFocus={!!errors.tablePaginationPageNo}
                                type="number"
                                requiredARIALabel={commonARIALabel.required}
                                error={!!errors.tablePaginationPageNo}
                                helperText={getHelperText()}
                                inputNumberWithoutDecimal
                                inputProps={{
                                  min: 1,
                                  "aria-label": commonARIALabel.pageNumber,
                                  "aria-required": "true",
                                }}
                              />
                            )}
                          </FormController>

                          <MuiButtonSecondary
                            className="submit-goto-page"
                            onClick={() => goToPage()}
                          >
                            {commonLabels.go}
                          </MuiButtonSecondary>
                        </MuiBox>
                      </MuiBox>
                    </TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          </MuiTableWrapper>
        )
      );
    };

    return (
      <TimelineActivityWrapper>
        <MuiBox className="activity-scroll-container">
          {activityData
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((row, index) => (
              <TimelineActivity
                id={row.id}
                handleEdit={handleEdit}
                handleDelete={handleDelete}
                key={`${row.title}-${index}`}
                timeStamp={getTimeStampValue(row)}
                isTodaySelected={isTodaySelected}
                title={row.title}
                creator={getCreatorValue(row)}
                displayWith={displayWith}
                expandableData={row.expandableData}
                isCollapsible={row.isCollapsible}
                showMoreOptions={row.showMoreOptions}
                isNotClosureType={row.isNotClosureType}
                hasDeletePermission={hasDeletePermission}
              />
            ))}
          {!isMobile && renderPagination()}
        </MuiBox>
        {isMobile && renderPagination()}
        {activityData.length === 0 && renderEmptyTableBody()}
      </TimelineActivityWrapper>
    );
  },
);
